import React, { useCallback, useEffect, useState } from 'react';
import { typesCourAPI, modalitesAPI } from '../../services/api';
import './ProgReferentiels.css';

/**
 * PROGRAMME DE FORMATION — RÉFÉRENTIELS (lot P.1)
 * ===============================================
 * Spec : doc/modules/programme_formation.md §9 · Maquette : doc/maquettes/programme_maquette.html (rév. 35, vue Référentiels)
 *
 * Catégories de cours (ce qu'EST un cours) et modalités (COMMENT ses heures se font), durables par département.
 * Les entrées fournies (RES, SAE, STAGE ; CM, TD, TP, PT) sont verrouillées : code, rôle et suppression bloqués ;
 * libellé, couleur, hTD et « Multiplié par » modifiables. Pas d'import (décision du 31/10/2026).
 * Chaque modification part tout de suite au serveur ; un échec s'affiche en rouge (jamais en silence).
 */

const CADRAGE_CATEGORIE = {
    ressource: "heures d'enseignement",
    sae: <>heures d'enseignement · ligne « dont SAÉ » (toujours <span className="pref-tag-l">L</span>)</>,
    stage: 'semaines de stage (en semaines, pas en heures)',
    hors: 'pas dans le cadrage'
};
const CADRAGE_MODALITE = {
    enseignement: "heures d'enseignement",
    enseignement_tp: "heures d'enseignement · ligne « TP » (minimum)",
    projet_tutore: <><span className="pref-tag-pt">PT</span> projet tutoré, <b>hors du total</b></>
};
const MULTIPLICATEURS = [['aucun', 'Aucun (×1)'], ['cm', 'Groupes CM'], ['td', 'Groupes TD'], ['tp', 'Groupes TP']];

const virgule = (n) => String(Number(n)).replace('.', ',');
const heure = () => new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const messageErreur = (e) => e?.response?.data?.error || e?.message || 'erreur inconnue';

function ProgReferentiels({ departementId, lectureSeule }) {
    const [onglet, setOnglet] = useState('types');
    const [types, setTypes] = useState([]);
    const [modalites, setModalites] = useState([]);
    const [chargement, setChargement] = useState(true);
    const [echecChargement, setEchecChargement] = useState(null);
    const [etat, setEtat] = useState(null);          // { ok: true, a: '14:32' } | { ok: false, message }
    const [fenetre, setFenetre] = useState(null);    // { quoi: 'types' | 'modalites', item | null }
    const [suppression, setSuppression] = useState(null); // { quoi, item, refus? }
    const [glisse, setGlisse] = useState(null);      // id de la modalité qu'on glisse

    const charger = useCallback(async () => {
        if (!departementId) return;
        setChargement(true);
        try {
            const [rt, rm] = await Promise.all([typesCourAPI.getAll(departementId), modalitesAPI.getAll(departementId)]);
            setTypes(rt.data.types_cours || []);
            setModalites(rm.data.modalites || []);
            setEchecChargement(null);
        } catch (e) {
            setEchecChargement(messageErreur(e));
        } finally {
            setChargement(false);
        }
    }, [departementId]);

    useEffect(() => { charger(); }, [charger]);

    /** Exécute une écriture, recharge, et dit le résultat (« Enregistré à … » ou l'échec en rouge). */
    const ecrire = async (faire) => {
        try {
            await faire();
            setEtat({ ok: true, a: heure() });
            await charger();
            return true;
        } catch (e) {
            setEtat({ ok: false, message: messageErreur(e) });
            await charger();
            return false;
        }
    };

    const majModalite = (m, champs) => ecrire(() => modalitesAPI.update(m.id, { departementId, ...champs }));

    const deposer = async (cible) => {
        const source = glisse;
        setGlisse(null);
        if (!source || source === cible.id) return;
        const liste = modalites.filter((m) => m.id !== source);
        const i = liste.findIndex((m) => m.id === cible.id);
        liste.splice(i, 0, modalites.find((m) => m.id === source));
        setModalites(liste);
        await ecrire(() => modalitesAPI.reorder(liste.map((m, k) => ({ id: m.id, ordre: k + 1 })), departementId));
    };

    const supprimer = async () => {
        const { quoi, item } = suppression;
        try {
            if (quoi === 'types') await typesCourAPI.delete(item.id, departementId);
            else await modalitesAPI.delete(item.id, departementId);
            setSuppression(null);
            setEtat({ ok: true, a: heure() });
            await charger();
        } catch (e) {
            setSuppression({ ...suppression, refus: messageErreur(e) });
        }
    };

    if (chargement && types.length === 0 && modalites.length === 0) {
        return <div className="pref-root pref-attente"><i className="fa-solid fa-spinner fa-spin" /> Chargement des référentiels…</div>;
    }

    return (
        <div className="pref-root" data-pref-onglet={onglet}>
            {echecChargement && (
                <div className="pref-echec" role="alert">
                    <i className="fa-solid fa-triangle-exclamation" /> Les référentiels n'ont pas pu être chargés : {echecChargement}
                    <button className="pref-btn pref-btn-sec pref-btn-sm" onClick={charger}>Réessayer</button>
                </div>
            )}

            <div className="pref-tabs">
                <button className={`pref-tab ${onglet === 'types' ? 'active' : ''}`} onClick={() => setOnglet('types')}>
                    Catégories de cours <span className="pref-count">{types.length}</span>
                </button>
                <button className={`pref-tab ${onglet === 'modalites' ? 'active' : ''}`} onClick={() => setOnglet('modalites')}>
                    Modalités &amp; groupes <span className="pref-count">{modalites.length}</span>
                </button>
            </div>

            <div className="pref-toolbar">
                {!lectureSeule && (
                    <button className="pref-btn pref-btn-pri pref-btn-sm" onClick={() => setFenetre({ quoi: onglet, item: null })}>
                        <i className="fa-solid fa-plus" /> Ajouter
                    </button>
                )}
                {lectureSeule && <span className="pref-lecture"><i className="fa-solid fa-eye" /> Lecture seule : votre compte peut consulter ce module, pas le modifier.</span>}
                <span className="pref-espace" />
                {etat && etat.ok && <span className="pref-etat-ok" data-pref-etat="ok"><i className="fa-solid fa-cloud" /> Enregistré à {etat.a}</span>}
                {etat && !etat.ok && (
                    <span className="pref-etat-ko" role="alert" data-pref-etat="ko">
                        <i className="fa-solid fa-triangle-exclamation" /> Non enregistré : {etat.message}
                        <button className="pref-x" title="Fermer" onClick={() => setEtat(null)}><i className="fa-solid fa-xmark" /></button>
                    </span>
                )}
            </div>

            {onglet === 'types' && (
                <table className="pref-tbl">
                    <thead><tr><th>Code</th><th>Libellé</th><th>Couleur</th><th>Dans le cadrage national</th><th className="pref-col-act">Actions</th></tr></thead>
                    <tbody>
                        {types.map((t) => (
                            <tr key={t.id} data-pref-type={t.code}>
                                <td><span className="pref-code">{t.code}</span></td>
                                <td>{t.nom}</td>
                                <td><span className="pref-dot" style={{ background: t.couleur }} />{t.couleur}</td>
                                <td className={`pref-cad ${t.role_cadrage === 'hors' ? 'hors' : ''}`}>{CADRAGE_CATEGORIE[t.role_cadrage]}</td>
                                <td className="pref-col-act">
                                    {!lectureSeule && (
                                        <button className="pref-act pref-act-edit" title={t.fournie ? 'Libellé et couleur modifiables' : 'Modifier'}
                                            onClick={() => setFenetre({ quoi: 'types', item: t })}><i className="fa-solid fa-pen" /></button>
                                    )}
                                    {t.fournie
                                        ? <span className="pref-lock" title="Fournie par l'application : ni supprimable, ni changée de rôle"><i className="fa-solid fa-lock" /> fournie</span>
                                        : !lectureSeule && (
                                            <button className="pref-act pref-act-del" title="Supprimer" onClick={() => setSuppression({ quoi: 'types', item: t })}>
                                                <i className="fa-solid fa-trash" />
                                            </button>
                                        )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {onglet === 'modalites' && (
                <>
                    <div className="pref-info">
                        <i className="fa-solid fa-circle-info" /> <b>Catégorie de cours</b> = ce qu'<b>est</b> le cours (une seule par cours) ·{' '}
                        <b>modalité</b> = <b>comment</b> ses heures se font (plusieurs par cours). Les modalités que le cadrage national compte
                        sont <b>fournies et verrouillées</b> (libellé, hTD et « Multiplié par » modifiables ; ni supprimables, ni changées de
                        rôle). Une modalité ajoutée par le département répond à une seule question : compte-t-elle dans les heures
                        d'enseignement ? « Multiplié par » donne le nombre de groupes qui multiplie ses heures dans le volume à réaliser.
                    </div>
                    {!lectureSeule && <div className="pref-astuce"><i className="fa-solid fa-grip-lines" /> Glissez les lignes pour modifier l'ordre d'affichage</div>}
                    {modalites.map((m) => (
                        <div key={m.id} className={`pref-row ${glisse === m.id ? 'glisse' : ''}`} data-pref-modalite={m.code}
                            draggable={!lectureSeule}
                            onDragStart={() => setGlisse(m.id)}
                            onDragOver={(e) => { if (glisse) e.preventDefault(); }}
                            onDrop={(e) => { e.preventDefault(); deposer(m); }}
                            onDragEnd={() => setGlisse(null)}>
                            {!lectureSeule && <span className="pref-poignee"><i className="fa-solid fa-grip-vertical" /></span>}
                            <span className="pref-code">{m.code}</span>
                            <span className="pref-nom">{m.nom}{!m.fournie && <span className="pref-hint"> (créée par le département)</span>}</span>
                            <span className="pref-htd">× {virgule(m.coefficient_htd)} hTD</span>
                            <label className="pref-mult" title={m.role_cadrage === 'projet_tutore' ? "Pas en présentiel : ni groupes, ni grille d'enseignants" : undefined}>
                                Multiplié par{' '}
                                {m.role_cadrage === 'projet_tutore' ? (
                                    <select className="pref-mult-sel" disabled><option>— pas de groupes</option></select>
                                ) : (
                                    <select className="pref-mult-sel" value={m.multiplicateur_groupe} disabled={lectureSeule}
                                        onChange={(e) => majModalite(m, { multiplicateur_groupe: e.target.value })}>
                                        {MULTIPLICATEURS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                )}
                            </label>
                            {m.fournie ? (
                                <span className="pref-cad pref-cad-mod">{CADRAGE_MODALITE[m.role_cadrage]}</span>
                            ) : (
                                <label className="pref-flag">
                                    <input type="checkbox" checked={m.role_cadrage === 'enseignement'} disabled={lectureSeule}
                                        onChange={(e) => majModalite(m, { compte_enseignement: e.target.checked })} />
                                    compte dans les heures d'enseignement
                                </label>
                            )}
                            {!lectureSeule && (
                                <button className="pref-act pref-act-edit" title={m.fournie ? 'Libellé et hTD modifiables' : 'Modifier'}
                                    onClick={() => setFenetre({ quoi: 'modalites', item: m })}><i className="fa-solid fa-pen" /></button>
                            )}
                            {m.fournie
                                ? <span className="pref-lock" title="Fournie par l'application : ni supprimable, ni changée de rôle"><i className="fa-solid fa-lock" /> fournie</span>
                                : !lectureSeule && (
                                    <button className="pref-act pref-act-del" title="Supprimer" onClick={() => setSuppression({ quoi: 'modalites', item: m })}>
                                        <i className="fa-solid fa-trash" />
                                    </button>
                                )}
                        </div>
                    ))}
                </>
            )}

            {fenetre && (
                <FenetreReferentiel fenetre={fenetre} departementId={departementId} onFermer={() => setFenetre(null)}
                    onEnregistre={async () => { setFenetre(null); setEtat({ ok: true, a: heure() }); await charger(); }} />
            )}

            {suppression && (
                <div className="pref-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setSuppression(null); }}>
                    <div className="pref-modal" role="dialog" data-pref-fenetre="suppression">
                        <div className="pref-modal-tete"><h3>Supprimer {suppression.quoi === 'types' ? 'la catégorie' : 'la modalité'} {suppression.item.code}</h3></div>
                        <div className="pref-modal-corps">
                            <p>« {suppression.item.nom} » sera supprimée définitivement.</p>
                            {suppression.refus && <p className="pref-refus" role="alert"><i className="fa-solid fa-triangle-exclamation" /> {suppression.refus}</p>}
                            <div className="pref-modal-actions">
                                <button className="pref-btn pref-btn-sec" onClick={() => setSuppression(null)}>Annuler</button>
                                {!suppression.refus && <button className="pref-btn pref-btn-dan" onClick={supprimer}>Confirmer la suppression</button>}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/** La fenêtre « Nouvelle catégorie / modalité » ou « Modifier » — un code fourni ne se change pas. */
function FenetreReferentiel({ fenetre, departementId, onFermer, onEnregistre }) {
    const { quoi, item } = fenetre;
    const categorie = quoi === 'types';
    const [code, setCode] = useState(item?.code || '');
    const [nom, setNom] = useState(item?.nom || '');
    const [couleur, setCouleur] = useState(item?.couleur || '#6C757D');
    const [htd, setHtd] = useState(item ? virgule(item.coefficient_htd) : '1');
    const [compte, setCompte] = useState(item ? item.role_cadrage === 'enseignement' : true);
    const [erreur, setErreur] = useState(null);
    const [envoi, setEnvoi] = useState(false);

    const enregistrer = async (e) => {
        e.preventDefault();
        setErreur(null);
        if (!code.trim() || !nom.trim()) { setErreur('Le code et le libellé sont obligatoires.'); return; }
        setEnvoi(true);
        try {
            const champs = categorie ? { nom, couleur } : { nom, coefficient_htd: htd };
            if (!item?.fournie) champs.code = code;
            if (!categorie && !item?.fournie) champs.compte_enseignement = compte;
            if (categorie) item ? await typesCourAPI.update(item.id, { departementId, ...champs }) : await typesCourAPI.create({ departement_id: departementId, ...champs });
            else item ? await modalitesAPI.update(item.id, { departementId, ...champs }) : await modalitesAPI.create({ departement_id: departementId, ...champs });
            await onEnregistre();
        } catch (er) {
            setErreur(messageErreur(er));
            setEnvoi(false);
        }
    };

    const titre = `${item ? 'Modifier' : 'Nouvelle'} ${categorie ? 'catégorie de cours' : 'modalité'}${item ? ' ' + item.code : ''}`;
    return (
        <div className="pref-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
            <form className="pref-modal" role="dialog" data-pref-fenetre={quoi} onSubmit={enregistrer}>
                <div className="pref-modal-tete">
                    <h3>{titre}</h3>
                    <button type="button" className="pref-x" title="Fermer" onClick={onFermer}><i className="fa-solid fa-xmark" /></button>
                </div>
                <div className="pref-modal-corps">
                    <div className="pref-champ">
                        <label>Code *</label>
                        <input value={code} maxLength={20} disabled={!!item?.fournie} onChange={(e) => setCode(e.target.value)}
                            placeholder={categorie ? 'ex : PROJ' : 'ex : VIS'} autoFocus={!item} />
                        {item?.fournie && <span className="pref-lock"><i className="fa-solid fa-lock" /> fournie : le code ne se change pas</span>}
                    </div>
                    <div className="pref-champ">
                        <label>Libellé *</label>
                        <input value={nom} maxLength={200} onChange={(e) => setNom(e.target.value)} autoFocus={!!item}
                            placeholder={categorie ? 'ex : Projet' : "ex : Visite d'entreprise"} />
                    </div>
                    {categorie ? (
                        <div className="pref-champ">
                            <label>Couleur</label>
                            <input type="color" value={couleur} onChange={(e) => setCouleur(e.target.value)} />
                        </div>
                    ) : (
                        <div className="pref-champ">
                            <label>Coefficient hTD</label>
                            <input value={htd} inputMode="decimal" onChange={(e) => setHtd(e.target.value)} placeholder="ex : 1,5" />
                        </div>
                    )}
                    {!categorie && !item?.fournie && (
                        <label className="pref-flag pref-flag-fen">
                            <input type="checkbox" checked={compte} onChange={(e) => setCompte(e.target.checked)} /> compte dans les heures d'enseignement
                        </label>
                    )}
                    {categorie && !item && <p className="pref-hint">Une catégorie ajoutée par le département ne compte pas dans le cadrage national.</p>}
                    {erreur && <p className="pref-refus" role="alert"><i className="fa-solid fa-triangle-exclamation" /> {erreur}</p>}
                    <div className="pref-modal-actions">
                        <button type="button" className="pref-btn pref-btn-sec" onClick={onFermer}>Annuler</button>
                        <button type="submit" className="pref-btn pref-btn-pri" disabled={envoi}>{envoi ? 'Enregistrement…' : 'Enregistrer'}</button>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default ProgReferentiels;
